import { MoveRight } from 'lucide-react';
import React, { useCallback, useEffect, useRef } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { theme } from 'styles/theme/theme';
import UDButton from '../button';

import { Divider } from '../divider';
import UDText from '../text';
import UDTippy from '../tippy';
import { UDTabProps } from './interfaces';
import { scrollTabIntoView } from './scroll-tab-into-view';

export const UdTab = ({ tab, activeRoutePattern }: UDTabProps) => {
  const navigate = useNavigate();
  const isActive = Boolean(useMatch(`${activeRoutePattern}/${tab.hash}/*`));
  const controlRef = useRef<HTMLDivElement>(null);

  // Вкладка в фокусе и активная (в том числе открытая по ссылке) видны в прокручиваемом ряду целиком.
  const scrollIntoRow = useCallback(() => {
    const control = controlRef.current;
    const row = control?.parentElement;
    if (control && row) scrollTabIntoView(control, row);
  }, []);

  useEffect(() => {
    if (isActive) scrollIntoRow();
  }, [isActive, scrollIntoRow]);

  const handleTabClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      const tabPath = e.currentTarget.dataset.hash;
      if (tabPath) {
        tab.onClick?.();
        navigate(tabPath, { replace: true, state: tab.state });
      }
    },
    [navigate, tab],
  );

  const button = (
    <UDButton
      key={tab.hash}
      disabled={tab.disabled}
      variant={isActive ? 'primary' : 'light'}
      data-hash={tab.hash}
      onClick={handleTabClick}
      type="button"
      className="flex items-center"
    >
      {tab.label}
      {tab.showArrow && !isActive && <MoveRight size={16} className="ml-2" color={theme.colors.SurfaceRomanSilver30} />}
    </UDButton>
  );

  return (
    <div ref={controlRef} className="control flex items-center" onFocus={scrollIntoRow}>
      {tab.disabled && tab.tooltip ? (
        <UDTippy content={tab.tooltip}>
          <div>{button}</div>
        </UDTippy>
      ) : (
        button
      )}
      {tab.subLabel && (
        <>
          <Divider type={'horizontal'} color={theme.colors.AccentNaplesYellow} height={'1px'} width={'17px'} />
          <UDButton
            key={tab.hash}
            disabled={tab.disabled}
            variant={'secondary'}
            data-hash={tab.hash}
            type="button"
            className="flex items-center"
          >
            <UDText type={'subhead'} maxWidth={145}>
              {tab.subLabel}
            </UDText>
          </UDButton>
        </>
      )}
    </div>
  );
};
